import React, { useState } from 'react';

const sectors = [
  'Manufacturing',
  'IT/ITeS',
  'Pharmaceuticals',
  'Automobile',
  'Textiles',
  'Food Processing',
  'Other'
];

const requiredDocuments = [
  'Company registration certificate',
  'Project report and feasibility study',
  'Financial statements (last 2 years)',
  'Environmental clearance plan',
  'Detailed project report',
  'Proof of identity of authorized signatory',
  'Bank reference letter'
];

const LandAllotmentForm = () => {
  const [formData, setFormData] = useState({
    representativeName: '',
    companyName: '',
    email: '',
    phone: '',
    sector: '',
    projectDetails: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.representativeName.trim()) newErrors.representativeName = 'Representative name is required';
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    if (!formData.sector) newErrors.sector = 'Industry sector is required';
    if (!formData.projectDetails.trim()) newErrors.projectDetails = 'Project details are required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      //console.log('Land Allotment Form submitted:', formData);
      // Simulate API call
      setTimeout(() => {
        setIsSubmitting(false);
        // Show success message or redirect
      }, 1500);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Land Allotment Application</h2>
      <p className="text-gray-600 mb-6">
        Apply for industrial land allotment with complete project details
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor="representativeName" className="block text-sm font-medium text-gray-700">
              Authorized Representative Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="representativeName"
              name="representativeName"
              value={formData.representativeName}
              onChange={handleChange}
              className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm ${
                errors.representativeName ? 'border-red-500' : ''
              }`}
              placeholder="Full name of authorized person"
            />
            {errors.representativeName && (
              <p className="mt-1 text-sm text-red-600">{errors.representativeName}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
              Company/Organization Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm ${
                errors.companyName ? 'border-red-500' : ''
              }`}
              placeholder="Registered company name"
            />
            {errors.companyName && (
              <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Official Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm ${
                errors.email ? 'border-red-500' : ''
              }`}
              placeholder="company@email.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
              Contact Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm ${
                errors.phone ? 'border-red-500' : ''
              }`}
              placeholder="+1 234 567 8900"
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <label htmlFor="sector" className="block text-sm font-medium text-gray-700">
              Industry Sector <span className="text-red-500">*</span>
            </label>
            <select
              id="sector"
              name="sector"
              value={formData.sector}
              onChange={handleChange}
              className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm ${
                errors.sector ? 'border-red-500' : ''
              }`}
            >
              <option value="">Select your industry sector</option>
              {sectors.map((sector) => (
                <option key={sector} value={sector}>
                  {sector}
                </option>
              ))}
            </select>
            {errors.sector && (
              <p className="mt-1 text-sm text-red-600">{errors.sector}</p>
            )}
          </div>
        </div>
        
        <div className="space-y-2">
          <label htmlFor="projectDetails" className="block text-sm font-medium text-gray-700">
            Project Details <span className="text-red-500">*</span>
          </label>
          <textarea
            id="projectDetails"
            name="projectDetails"
            rows={6}
            value={formData.projectDetails}
            onChange={handleChange}
            className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm ${
              errors.projectDetails ? 'border-red-500' : ''
            }`}
            placeholder="Please provide details: land area requirement, investment amount, employment generation, production capacity, timeline, etc."
          />
          {errors.projectDetails && (
            <p className="mt-1 text-sm text-red-600">{errors.projectDetails}</p>
          )}
        </div>
        
        <div className="bg-gray-50 p-4 rounded-md">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Required Documents</h3>
          <p className="text-sm text-gray-600 mb-3">
            The following documents will be required during the review process:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
            {requiredDocuments.map((doc, index) => (
              <li key={index}>{doc}</li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-gray-500">
            Note: All documents should be in PDF format and not exceed 5MB each.
          </p>
        </div>
        
        <div className="flex items-center">
          <input
            id="terms"
            name="terms"
            type="checkbox"
            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            required
          />
          <label htmlFor="terms" className="ml-2 block text-sm text-gray-700">
            I agree to the <a href="/terms" className="text-purple-600 hover:text-purple-500">terms and conditions</a>
          </label>
        </div>
        
        <div className="pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full md:w-auto px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-200 ${
              isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LandAllotmentForm;
